<template>
  <div id="app">
    <!-- Loading state while checking authentication -->
    <div v-if="isLoading" class="loading-container">
      <LinkflareLoader class="loading-spinner" :label="$t('common.loading')" size="2rem" />
    </div>

    <!-- Authenticated layout -->
    <div v-else-if="isSignedIn && requiresAuth" class="layout-wrapper">
      <a href="#main-content" class="skip-link">{{ $t('app.skipToMain') }}</a>
      <!-- Header -->
      <header class="app-header">
        <div class="header-content">
          <div class="logo">
            <img :src="isDarkMode ? '/linkflare-text-white.svg' : '/linkflare-text-black.svg'" alt="linkflare Logo"
              class="header-logo header-logo-text" />
            <img src="/linkflare.svg" alt="linkflare Logo" class="header-logo header-logo-icon" />
          </div>
          <!-- `active-class` only fires when the link's OWN route record is
               matched. Sub-routes declared as separate entries in main.js
               (/knowledge/review, /admin/:tab, …) are SIBLING records, not
               children, so the parent item would go unhighlighted there — hence
               the explicit `startsWith` binding on every nav target that has
               sub-routes. tests/navActiveSubRoutes.test.js derives the list from
               the route table, so a newly added sub-route fails the suite until
               its nav item is covered here. -->
          <nav class="main-nav">
            <router-link to="/bookmarks" class="nav-link" active-class="active">
              <i class="pi pi-bookmark"></i>
              <span class="nav-label">{{ $t('app.nav.bookmarks') }}</span>
            </router-link>
            <router-link to="/feeds" class="nav-link" active-class="active">
              <Rss :size="16" />
              <span class="nav-label">{{ $t('app.nav.feeds') }}</span>
            </router-link>
            <router-link to="/knowledge" class="nav-link" active-class="active"
                         :class="{ active: $route.path.startsWith('/knowledge/') }">
              <Brain :size="16" />
              <span class="nav-label">{{ $t('app.nav.knowledge') }}</span>
              <span v-if="!isPro" class="nav-pro-badge">PRO</span>
            </router-link>
            <router-link to="/highlights" class="nav-link" active-class="active">
              <Highlighter :size="16" />
              <span class="nav-label">{{ $t('app.nav.highlights') }}</span>
            </router-link>
            <router-link v-if="isAdmin" to="/admin" class="nav-link" active-class="active"
                         :class="{ active: $route.path.startsWith('/admin/') }">
              <BarChart3 :size="16" />
              <span class="nav-label">{{ $t('app.nav.admin') }}</span>
            </router-link>
          </nav>
          <div class="search-section">
            <SearchBar />
          </div>
          <div class="user-section">
            <div v-if="bellBadgeCount > 0 || pendingInvitations.length > 0 || activityNotifications.length > 0" class="invitation-bell">
              <Button class="p-button-text invitation-bell-btn"
                @click="toggleNotificationsPanel">
                <template #icon>
                  <Bell :size="18" />
                </template>
              </Button>
              <span v-if="bellBadgeCount > 0" class="invitation-badge">{{ bellBadgeCount }}</span>
              <div v-if="showInvitationsPanel" class="invitations-panel" @click.stop>
                <div v-if="activityNotifications.length > 0" class="activity-section">
                  <div class="activity-section-header">
                    <h4><Users :size="14" /> {{ $t('app.notifications.recentActivity') }}</h4>
                    <button v-if="activityNotifications.length > 1" class="mark-all-seen-btn" @click.stop="dismissAllActivity">{{ $t('app.notifications.markAllSeen') }}</button>
                  </div>
                  <div v-for="notif in activityNotifications" :key="notif.id" class="activity-card"
                    @click="navigateToActivityLink(notif)">
                    <div class="activity-card-body">
                      <div class="activity-card-message">{{ notif.message }}</div>
                      <div class="activity-card-time">{{ formatRelativeTime(notif.sent_at) }}</div>
                    </div>
                    <button class="activity-dismiss-btn" @click.stop="dismissActivity(notif)" :title="$t('app.notifications.markSeen')">×</button>
                  </div>
                  <button class="view-all-link" @click="navigateToNotifications('activity'); showInvitationsPanel = false">{{ $t('app.notifications.viewAllActivity') }}</button>
                </div>
                <div v-if="pendingInvitations.length > 0" class="invitations-section">
                  <h4>{{ $t('app.notifications.pendingInvitations') }}</h4>
                  <div v-for="invite in pendingInvitations" :key="invite.id" class="invitation-card">
                    <div class="invitation-card-info">
                      <strong>{{ invite.collection_name }}</strong>
                      <span class="invitation-card-detail">{{ $t('app.notifications.invitationDetail', {
                        name: invite.inviter_name,
                        role: invite.role === 'READ_ONLY' ? $t('app.notifications.roleView') : invite.role === 'ADD' ? $t('app.notifications.roleAdd') : $t('app.notifications.roleFull')
                      }) }}</span>
                    </div>
                    <div class="invitation-card-actions">
                      <Button :label="$t('common.actions.accept')" class="p-button-sm p-button-success"
                        @click="handleAcceptInvitation(invite)" />
                      <Button :label="$t('common.actions.decline')" class="p-button-sm p-button-text"
                        @click="handleDeclineInvitation(invite)" />
                    </div>
                  </div>
                </div>
                <div v-if="activityNotifications.length === 0 && pendingInvitations.length === 0" class="empty-panel">
                  <p>{{ $t('app.notifications.empty') }}</p>
                </div>
              </div>
            </div>
            <Button :icon="privateModeEnabled ? undefined : 'pi pi-user'" class="p-button-text user-button" @click="toggleUserMenu">
              <template v-if="privateModeEnabled" #icon>
                <UserLock :size="18" />
              </template>
            </Button>
            <div v-if="showUserMenu" class="user-menu">
              <button class="user-menu-close-btn" @click="showUserMenu = false">
                <i class="pi pi-times"></i>
              </button>
              <div class="user-info">
                <strong>{{ user?.firstName }} {{ user?.lastName }}</strong>
                <span>{{ userEmail }}</span>
                <div class="username-row">
                  <span class="username-display">@{{ backendUser?.username || '...' }}</span>
                </div>
              </div>
              <Button :label="$t('app.userMenu.profile')" icon="pi pi-user-edit"
                class="p-button-text p-button-sm user-menu-action-btn" @click="openProfileModal" />
              <hr>
              <div class="theme-toggle-section">
                <SelectButton v-model="themeOption" :options="themeOptions" @change="onThemeChange" optionLabel="label"
                  optionValue="value" class="theme-toggle">
                  <template #option="slotProps">
                    <i :class="slotProps.option.icon"></i>
                    <span class="option-label">{{ slotProps.option.label }}</span>
                  </template>
                </SelectButton>
              </div>
              <hr>
              <div class="private-mode-section">
                <SelectButton v-model="privateModeOption" :options="privateModeOptions" @change="onPrivateModeChange"
                  optionLabel="label" optionValue="value" class="private-mode-toggle">
                  <template #option="slotProps">
                    <i :class="slotProps.option.icon"></i>
                    <span class="option-label">{{ slotProps.option.label }}</span>
                  </template>
                </SelectButton>
              </div>
              <hr>
              <div class="import-menu-wrapper">
                <Button :label="$t('app.userMenu.import')" icon="pi pi-upload"
                  class="p-button-text p-button-sm import-button user-menu-action-btn" @click="toggleImportMenu($event)"
                  aria-haspopup="true" aria-controls="import_tiered_menu" />
                <TieredMenu id="import_tiered_menu" :model="importMenuItems" popup ref="importMenuRef" />
              </div>
              <Button :label="$t('app.userMenu.export')" icon="pi pi-download"
                class="p-button-text p-button-sm import-button user-menu-action-btn" @click="openExportModal" />
              <Button :label="$t('app.userMenu.keyboardShortcuts')" icon="pi pi-question-circle"
                class="p-button-text p-button-sm user-menu-action-btn" @click="openKeyboardShortcuts" />
              <Button :label="$t('app.userMenu.changelog')" icon="pi pi-sparkles" class="p-button-text p-button-sm user-menu-action-btn"
                @click="openChangelog" />
              <hr>
              <Button :label="$t('app.userMenu.tags')" icon="pi pi-tags" class="p-button-text p-button-sm user-menu-action-btn"
                @click="navigateToTags" />
              <Button :label="$t('app.userMenu.rules')" icon="pi pi-directions" class="p-button-text p-button-sm user-menu-action-btn"
                @click="navigateToRules" />
              <Button :label="$t('app.userMenu.notifications')" icon="pi pi-bell" class="p-button-text p-button-sm user-menu-action-btn"
                @click="navigateToNotifications" />
              <Button :label="$t('app.userMenu.myStats')" icon="pi pi-chart-pie" class="p-button-text p-button-sm user-menu-action-btn"
                @click="navigateToStats" />
              <hr>
              <div v-if="isPro" class="pro-badge">
                <i class="pi pi-verified"></i> PRO
              </div>
              <Button :label="isPro ? $t('app.userMenu.manageSubscription') : $t('app.userMenu.upgradeToPro')" icon="pi pi-credit-card"
                class="p-button-text p-button-sm user-menu-action-btn" @click="openSubscriptionModal" />
              <Button :label="$t('app.userMenu.copyApiKey')" icon="pi pi-key" class="p-button-text p-button-sm user-menu-action-btn"
                @click="copyApiKey" />
              <hr>
              <Button :label="$t('app.userMenu.signOut')" icon="pi pi-sign-out" class="p-button-text p-button-sm user-menu-action-btn"
                @click="handleSignOut" />
            </div>
          </div>
        </div>
      </header>

      <!-- Import Progress Indicator -->
      <ImportProgressIndicator v-if="hasActiveImportTask" :current-task="currentImportTask"
        :progress-percent="importProgressPercent" :urls-imported="urlsImported"
        :estimated-time-remaining="estimatedTimeRemaining" @cancel="cancelImportTask" />
      <ImportSummaryBanner v-else-if="importSummaryToShow" :result="importSummaryToShow"
        @dismiss="dismissImportResult" @upgrade="openSubscriptionModal" />

      <!-- Browser extension nudge (hides itself once installed/dismissed) -->
      <ExtensionPrompt />

      <!-- Feedback Banner -->
      <FeedbackBanner />

      <!-- Main Content -->
      <main id="main-content" class="app-main" tabindex="-1">
        <router-view />
      </main>

      <!-- Install Prompt -->
      <InstallPrompt />
    </div>

    <!-- Public pages (when signed in but viewing public page) -->
    <div v-else-if="isSignedIn && !requiresAuth">
      <router-view />
    </div>

    <!-- Unauthenticated state - show router view for sign in/up/public pages -->
    <div v-else>
      <router-view />
    </div>

    <!-- Global Toast and Confirmation Dialog -->
    <Toast />
    <!-- Undo toast for reversible quick actions (useUndoToast.js). #container replaces
         the whole message so the Undo button sits inline with the text. -->
    <Toast group="undo" position="bottom-center" class="undo-toast">
      <template #container="{ message, closeCallback }">
        <div class="undo-toast-body" role="status">
          <i class="pi pi-check-circle undo-toast-icon" aria-hidden="true"></i>
          <span class="undo-toast-text">{{ message.summary }}</span>
          <Button
            v-if="message.undo"
            :label="$t('quickActions.undo')"
            class="p-button-text p-button-sm undo-toast-button"
            @click="runUndo(message, closeCallback)"
          />
          <Button
            icon="pi pi-times"
            class="p-button-text p-button-sm p-button-rounded undo-toast-close"
            :aria-label="$t('common.actions.close')"
            @click="closeCallback"
          />
        </div>
      </template>
    </Toast>
    <ConfirmDialog />

    <!-- Service-worker update prompt (shown in all auth states) -->
    <PwaUpdatePrompt />

    <!-- Import Modals -->
    <ImportModal v-if="showImportModalMounted" v-model:visible="showImportModal" @import-completed="handleImportCompleted" />
    <ImportChromeModal v-if="showChromeImportModalMounted" v-model:visible="showChromeImportModal" @import-completed="handleImportCompleted" />
    <ImportUrlListModal v-if="showUrlListImportModalMounted" v-model:visible="showUrlListImportModal" @import-completed="handleImportCompleted" />
    <ImportSourceModal v-if="showImportSourceModalMounted" source="pocket" v-model:visible="showPocketImportModal"
      @import-completed="handleImportCompleted" />
    <ImportSourceModal v-if="showImportSourceModalMounted" source="instapaper" v-model:visible="showInstapaperImportModal"
      @import-completed="handleImportCompleted" />
    <ImportSourceModal v-if="showImportSourceModalMounted" source="goodreads" v-model:visible="showGoodreadsImportModal"
      @import-completed="handleImportCompleted" />
    <ImportSourceModal v-if="showImportSourceModalMounted" source="youtube" v-model:visible="showYoutubeImportModal"
      @import-completed="handleImportCompleted" />
    <ImportSourceModal v-if="showImportSourceModalMounted" source="karakeep" v-model:visible="showKarakeepImportModal"
      @import-completed="handleImportCompleted" />
    <ImportSourceModal v-if="showImportSourceModalMounted" source="linkwarden" v-model:visible="showLinkwardenImportModal"
      @import-completed="handleImportCompleted" />
    <ExportModal v-if="showExportModalMounted" v-model:visible="showExportModal" />
    <SubscriptionModal
      v-if="showSubscriptionModalMounted"
      v-model:visible="showSubscriptionModal"
      :is-pro="isPro"
      :cancels-at="cancelsAt"
      :promo-expires-at="promoExpiresAt"
      @subscription-updated="onSubscriptionUpdated"
    />

    <!-- Protection Password Modal -->
    <Dialog v-model:visible="showProtectionPasswordModal" modal :header="$t('app.protectionPassword.title')"
      :style="{ width: '450px' }" @show="onShowProtectionPasswordModal">
      <div class="protection-password-form">
        <p class="modal-description">
          {{ $t('app.protectionPassword.description') }}
        </p>
        <div class="field">
          <label for="newPassword">{{ $t('app.protectionPassword.newPassword') }}</label>
          <InputText ref="newPasswordInputRef" id="newPassword" v-model="protectionPasswordForm.password"
            type="password" :placeholder="$t('app.protectionPassword.passwordPlaceholder')" class="w-full" />
        </div>
        <div class="field">
          <label for="confirmPassword">{{ $t('app.protectionPassword.confirmPassword') }}</label>
          <InputText id="confirmPassword" v-model="protectionPasswordForm.confirmPassword" type="password"
            :placeholder="$t('app.protectionPassword.confirmPlaceholder')" class="w-full" @keyup.enter="saveProtectionPassword" />
        </div>
        <p v-if="protectionPasswordError" class="password-error">{{ protectionPasswordError }}</p>
      </div>

      <template #footer>
        <Button :label="$t('common.actions.cancel')" icon="pi pi-times" @click="cancelProtectionPassword" class="p-button-text" />
        <Button :label="$t('common.actions.save')" icon="pi pi-check" @click="saveProtectionPassword"
          :disabled="!protectionPasswordForm.password || !protectionPasswordForm.confirmPassword"
          :loading="savingProtectionPassword" />
      </template>
    </Dialog>

    <!-- Keyboard Shortcuts Help -->
    <ShortcutHelpDialog v-if="showShortcutHelpMounted" v-model:visible="showShortcutHelp" :shortcuts="shortcutMap" />

    <!-- Profile Settings Modal (username, language, delete account) -->
    <ProfileSettingsModal v-if="showProfileModalMounted" v-model:visible="showProfileModal"
      :current-username="backendUser?.username || ''" :display-name="userDisplayName" :email="userEmail" :is-pro="isPro"
      @username-changed="onUsernameChanged" @language-changed="onLanguageChangedFromProfile" />

    <!-- Onboarding Flow -->
    <OnboardingFlow v-if="showOnboardingMounted" :visible="showOnboarding" @complete="showOnboarding = false"
      @open-import="handleOnboardingImport" />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick, provide, reactive } from 'vue'
import { lazyComponent, CHUNK_LOAD_FAILED_EVENT } from './utils/lazyComponent.js'
import { useRouter, useRoute } from 'vue-router'
import { isDevMode, useDevAuth } from './composables/devAuth.js'
// Clerk useAuth is only called when NOT in dev mode (guarded in setup)
import { useAuth as useClerkAuth } from '@clerk/vue'
import Button from 'primevue/button'
import TieredMenu from 'primevue/tieredmenu'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import SelectButton from 'primevue/selectbutton'
import ToggleButton from 'primevue/togglebutton'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'petite-vue-i18n'
import InstallPrompt from './components/InstallPrompt.vue'
import SearchBar from './components/SearchBar.vue'
import ImportProgressIndicator from './components/ImportProgressIndicator.vue'
import ImportSummaryBanner from './components/ImportSummaryBanner.vue'
import FeedbackBanner from './components/FeedbackBanner.vue'
import ExtensionPrompt from './components/ExtensionPrompt.vue'
import PwaUpdatePrompt from './components/PwaUpdatePrompt.vue'
import LinkflareLoader from './components/LinkflareLoader.vue'
import { useLinkflareAuth } from './composables/useAuth.js'
import { useTheme } from './composables/useTheme.js'
import { useLocale, peekSignupLanguage, clearSignupLanguage } from './composables/useLocale.js'
import { useLocaleFormat } from './composables/useLocaleFormat.js'
import { useApiErrorHandler } from './composables/useApiErrorHandler.js'
import { useImportTask } from './composables/useImportTask.js'
import { useImportMenu, OPEN_IMPORT_MODAL_EVENT } from './composables/useImportMenu.js'
import { useKeyboardShortcuts } from './composables/useKeyboardShortcuts.js'
import { markOnboardingComplete, isOnboardingCompletedLocally } from './utils/onboardingCompletion.js'
import { consumeImportIntent } from './utils/importIntent.js'
import { getSavedLastRoute } from './utils/lastRoute.js'
import { isCleanImport, importSummaryMessages, formatImportSummaryLine } from './utils/importSummary.js'
import ApiService from './services/api.js'
import ReminderService from './services/reminder.js'
// Sentry user functions are lazy-loaded to keep the 518KB sentry chunk deferred
const setSentryUser = (user) => import('./services/sentry.js').then(m => m.setSentryUser(user))
const clearSentryUser = () => import('./services/sentry.js').then(m => m.clearSentryUser())

// Analytics is lazy-loaded for the same reason as Sentry — these wrappers keep
// it off the startup path and swallow their own failures.
const trackEvent = (name, props) => import('./services/analytics.js').then(m => m.trackEvent(name, props)).catch(() => { })
const trackOnce = (key, name, props) => import('./services/analytics.js').then(m => m.trackOnce(key, name, props)).catch(() => { })

// How recently the Clerk account must have been created for an unfinished
// onboarding to count as a signup. Generous on purpose: the tour can be
// abandoned and resumed the next day, and trackOnce dedupes the repeats.
const SIGNUP_MAX_ACCOUNT_AGE_MS = 24 * 60 * 60 * 1000
import { clearBadge } from './services/appBadge.js'
import offlineSyncQueue from './services/offlineSyncQueue.js'
import { Rss, SignpostBig, UserLock, Bell, Users, Brain, Highlighter, BarChart3 } from 'lucide-vue-next'

// Every lazily-mounted modal goes through `lazyComponent`, never bare
// `defineAsyncComponent`: a bare one renders nothing when its chunk fails to
// load, which presents as a permanently dead button with no error anywhere.
// That is the 2026-07-14 App Review 2.1(a) rejection.
const ImportModal = lazyComponent('ImportModal', () => import('./components/ImportModal.vue'))
const ImportChromeModal = lazyComponent('ImportChromeModal', () => import('./components/ImportChromeModal.vue'))
const ImportUrlListModal = lazyComponent('ImportUrlListModal', () => import('./components/ImportUrlListModal.vue'))
const ImportSourceModal = lazyComponent('ImportSourceModal', () => import('./components/ImportSourceModal.vue'))
const ExportModal = lazyComponent('ExportModal', () => import('./components/ExportModal.vue'))
const SubscriptionModal = lazyComponent('SubscriptionModal', () => import('./components/SubscriptionModal.vue'))
const ShortcutHelpDialog = lazyComponent('ShortcutHelpDialog', () => import('./components/ShortcutHelpDialog.vue'))
const OnboardingFlow = lazyComponent('OnboardingFlow', () => import('./components/OnboardingFlow.vue'))
const ProfileSettingsModal = lazyComponent('ProfileSettingsModal', () => import('./components/ProfileSettingsModal.vue'))

export default {
  name: 'App',
  components: {
    Button,
    Rss,
    SignpostBig,
    UserLock,
    Bell,
    Users,
    Brain,
    Highlighter,
    BarChart3,
    TieredMenu,
    Toast,
    ConfirmDialog,
    SelectButton,
    ToggleButton,
    Dialog,
    InputText,
    InstallPrompt,
    SearchBar,
    ImportModal,
    ImportChromeModal,
    ImportUrlListModal,
    ImportSourceModal,
    ImportProgressIndicator,
    ImportSummaryBanner,
    ExportModal,
    SubscriptionModal,
    FeedbackBanner,
    ExtensionPrompt,
    PwaUpdatePrompt,
    LinkflareLoader,
    ShortcutHelpDialog,
    OnboardingFlow,
    ProfileSettingsModal
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    // In dev mode, use mock auth; otherwise use Clerk
    const { isLoaded, isSignedIn } = isDevMode ? useDevAuth() : useClerkAuth()
    const { user, userDisplayName, userEmail, handleSignOut: authSignOut, getAuthToken } = useLinkflareAuth()
    const { isDarkMode, themePreference, toggleTheme, setTheme, initializeTheme } = useTheme()
    const { handleApiError, clearApiKey } = useApiErrorHandler()
    const toast = useToast()
    // Undo button on the group="undo" toast: close first so a slow inverse PUT
    // cannot be clicked twice; the action's own handler reports failures.
    const runUndo = (message, closeCallback) => {
      if (typeof closeCallback === 'function') closeCallback()
      if (message && typeof message.undo === 'function') {
        Promise.resolve(message.undo()).catch(() => { /* reported by the caller */ })
      }
    }
    const { t } = useI18n()
    const { currentLocale, setLocale: setAppLocale, bindPrimeVue } = useLocale()
    // Lets useLocale swap the PrimeVue component locale at runtime (needs app context)
    bindPrimeVue()
    const { formatDate: formatLocalizedDate, formatNumber: formatLocalizedNumber } = useLocaleFormat()

    // Keyboard shortcuts
    const { showHelpDialog: showShortcutHelp, shortcuts: shortcutMap } = useKeyboardShortcuts()

    const openKeyboardShortcuts = () => {
      showUserMenu.value = false
      showShortcutHelp.value = true
    }

    const openChangelog = () => {
      showUserMenu.value = false
      window.open('https://linkflare.io/changelog/', '_blank')
    }

    const navigateToStats = () => {
      showUserMenu.value = false
      router.push('/dashboard')
    }

    const navigateToTags = () => {
      showUserMenu.value = false
      router.push('/tags')
    }

    const navigateToRules = () => {
      showUserMenu.value = false
      router.push('/rules')
    }

    const navigateToNotifications = (tab = null) => {
      showUserMenu.value = false
      router.push(tab ? `/notifications?tab=${tab}` : '/notifications')
    }

    const backendUser = ref(null)

    // Start reminder service when authenticated
    watch([isSignedIn, isLoaded], async ([signedIn, loaded]) => {
      if (loaded && signedIn) {
        try {
          const token = await getAuthToken()
          if (token) {
            ReminderService.start(token)

            // Replay any offline-queued bookmark saves when connectivity returns
            offlineSyncQueue.startOnlineListener(
              (linkData, t, runRules) => ApiService.saveLink(linkData, t, runRules, { background: true }),
              getAuthToken
            )
            // Clear previous callbacks to prevent duplicates if this watcher re-fires
            offlineSyncQueue.clearCallbacks()
            offlineSyncQueue.onSync((count) => {
              toast.add({
                severity: 'success',
                summary: t('app.toasts.offlineSyncedTitle'),
                detail: t('app.toasts.offlineSyncedDetail', count),
                life: 4000
              })
            })
            // Flush any pending saves immediately if we're already online.
            // Fire-and-forget: flush() degrades to a no-op when IndexedDB is
            // unavailable, and the explicit .catch() guards against any other
            // late rejection so it can never escape as an unhandled rejection
            // (Sentry 130854759).
            offlineSyncQueue.flush(
              (linkData, t, runRules) => ApiService.saveLink(linkData, t, runRules, { background: true }),
              getAuthToken
            ).catch((e) => console.warn('Offline flush failed:', e))
            
            // Reminder → toast wiring is registered once in onMounted via the named
            // handleReminder handler (see below). Registering it here would add a
            // duplicate listener on every auth transition and leak it (H6).
          }
        } catch (e) {
          console.error("Error starting reminder service:", e)
        }
      } else if (loaded && !signedIn) {
        ReminderService.stop()
      }
    }, { immediate: true })

    const isPro = computed(() => {
      const plan = backendUser.value?.plan || user.value?.plan
      return plan && plan.toLowerCase() === 'pro'
    })

    // Admin nav visibility — single hard-coded email allowlist, mirrored on
    // the server (LinkflareAuthorizationResource.ADMIN_EMAIL).
    const ADMIN_EMAIL = 'davidurbansky@gmail.com'
    const isAdmin = computed(() => {
      const beEmail = backendUser.value?.email
      const clerkEmail = userEmail?.value
      const email = (beEmail || clerkEmail || '').toLowerCase()
      return email === ADMIN_EMAIL
    })

    const cancelsAt = computed(() => {
      return backendUser.value?.cancels_at
    })

    // Set only while an active promo redemption grants Pro (GET /users enrichment);
    // lets SubscriptionModal keep the checkout visible for promo users.
    const promoExpiresAt = computed(() => {
      return backendUser.value?.promo_expires_at
    })

    provide('isPro', isPro)
    provide('backendUser', backendUser)

    const pendingInvitations = ref([])
    provide('pendingInvitations', pendingInvitations)

    const loadPendingInvitations = async (token) => {
      try {
        const resp = await ApiService.getMyInvitations(token)
        pendingInvitations.value = resp?.invitations || []
      } catch (e) {
        console.warn('Failed to load pending invitations', e)
      }
    }

    // Activity notifications for shared collections
    const activityNotifications = ref([])
    let activityPollTimer = null

    const getLastSeenActivityAt = () => {
      try {
        return localStorage.getItem('linkflare_lastSeenActivityAt') || null
      } catch { return null }
    }

    const setLastSeenActivityAt = () => {
      try {
        localStorage.setItem('linkflare_lastSeenActivityAt', new Date().toISOString())
      } catch { /* ignore */ }
    }

    const DISMISSED_IDS_KEY = 'linkflare_dismissedActivityIds'

    const getDismissedActivityIds = () => {
      try {
        const raw = localStorage.getItem(DISMISSED_IDS_KEY)
        if (!raw) return new Set()
        const arr = JSON.parse(raw)
        return new Set(Array.isArray(arr) ? arr : [])
      } catch { return new Set() }
    }

    const setDismissedActivityIds = (set) => {
      try {
        localStorage.setItem(DISMISSED_IDS_KEY, JSON.stringify(Array.from(set)))
      } catch { /* ignore */ }
    }

    const loadActivityNotifications = async (token) => {
      try {
        const resp = await ApiService.getNotifications({ type: 'COLLECTION_ACTIVITY', limit: 10 }, token, { background: true })
        const fromServer = resp?.notifications || []
        const dismissed = getDismissedActivityIds()
        if (dismissed.size > 0) {
          // Prune dismissed IDs that no longer appear in the server response,
          // so the set stays bounded as old notifications age out of the top-N.
          const serverIds = new Set(fromServer.map(n => n.id))
          const pruned = new Set(Array.from(dismissed).filter(id => serverIds.has(id)))
          if (pruned.size !== dismissed.size) setDismissedActivityIds(pruned)
          activityNotifications.value = fromServer.filter(n => !pruned.has(n.id))
        } else {
          activityNotifications.value = fromServer
        }
      } catch (e) {
        console.warn('Failed to load activity notifications', e)
      }
    }

    const unreadActivityCount = computed(() => {
      const lastSeen = getLastSeenActivityAt()
      if (!lastSeen) return activityNotifications.value.length
      const lastSeenDate = new Date(lastSeen)
      return activityNotifications.value.filter(n => new Date(n.sent_at) > lastSeenDate).length
    })

    const bellBadgeCount = computed(() => {
      return pendingInvitations.value.length + unreadActivityCount.value
    })

    const toggleNotificationsPanel = () => {
      showInvitationsPanel.value = !showInvitationsPanel.value
      if (showInvitationsPanel.value) {
        // Mark activity as seen when panel opens
        setLastSeenActivityAt()
      }
    }

    const navigateToActivityLink = (notif) => {
      showInvitationsPanel.value = false
      if (notif.link_id) {
        // Open the link in a read-only detail modal (fetched by id) rather than
        // scrolling to a card that may not be in the current filtered/inbox list.
        router.push({ path: '/bookmarks', query: { viewLink: notif.link_id } })
      }
    }

    const dismissActivity = (notif) => {
      if (notif?.id != null) {
        const dismissed = getDismissedActivityIds()
        dismissed.add(notif.id)
        setDismissedActivityIds(dismissed)
      }
      const idx = activityNotifications.value.findIndex(n => n.id === notif.id)
      if (idx !== -1) activityNotifications.value.splice(idx, 1)
      setLastSeenActivityAt()
    }

    const dismissAllActivity = () => {
      const dismissed = getDismissedActivityIds()
      for (const n of activityNotifications.value) {
        if (n?.id != null) dismissed.add(n.id)
      }
      setDismissedActivityIds(dismissed)
      activityNotifications.value = []
      setLastSeenActivityAt()
    }

    const formatRelativeTime = (dateStr) => {
      if (!dateStr) return ''
      const now = new Date()
      const date = new Date(dateStr)
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      if (diffMins < 1) return t('app.time.justNow')
      if (diffMins < 60) return t('app.time.minutesAgo', { n: diffMins })
      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return t('app.time.hoursAgo', { n: diffHours })
      const diffDays = Math.floor(diffHours / 24)
      if (diffDays < 7) return t('app.time.daysAgo', { n: diffDays })
      return formatLocalizedDate(date)
    }

    const startActivityPolling = async () => {
      const token = await getAuthToken()
      if (token) {
        loadActivityNotifications(token)
        activityPollTimer = setInterval(async () => {
          const t = await getAuthToken()
          if (t) loadActivityNotifications(t)
        }, 60000) // poll every 60 seconds
      }
    }

    onUnmounted(() => {
      if (activityPollTimer) clearInterval(activityPollTimer)
    })

    const userStats = reactive({
      bookmarkCount: 0,
      feedCount: 0,
      // Link types present in the user's library (e.g. ['ARTICLE','RECIPE']).
      // The /links `types` facet covers ALL matching links regardless of the
      // request's `limit`, so the limit=1 call below carries it for free.
      // Gates the Bookmarks sidebar Tools section (utils/toolAvailability.js).
      linkTypes: [],
      // `bookmarkCount` starts at 0 and this request may fail silently, so
      // consumers cannot tell "empty library" from "not fetched yet" without
      // it. The activation milestone depends on that distinction (see
      // utils/activation.js).
      statsLoaded: false
    })
    provide('userStats', userStats)

    const fetchUserStats = async (token) => {
      try {
        // Get bookmark count (using limit=1 to get total)
        const linksResp = await ApiService.getLinks({ limit: 1 }, token)
        if (linksResp && linksResp.total !== undefined) {
          userStats.bookmarkCount = linksResp.total
          userStats.statsLoaded = true
        }
        // The zero-link early return backend-side may omit the facet — keep [].
        if (Array.isArray(linksResp?.types)) {
          userStats.linkTypes = linksResp.types
        }

        // Get feed count
        const feedsResp = await ApiService.getFeedSubscriptions(token)
        if (Array.isArray(feedsResp)) {
          userStats.feedCount = feedsResp.length
        }
      } catch (e) {
        console.error('Failed to fetch user stats', e)
      }
    }

    // Import task tracking
    const {
      currentTask: currentImportTask,
      hasActiveTask: hasActiveImportTask,
      progressPercent: importProgressPercent,
      urlsImported,
      estimatedTimeRemaining,
      startTask: startImportTask,
      cancelTask: cancelImportTask,
      importResult,
      dismissResult: dismissImportResult,
      forget: forgetImport
    } = useImportTask(getAuthToken)

    // An import that left links out keeps a banner until dismissed; a clean
    // one (everything landed or was already saved) only gets a toast.
    const importSummaryToShow = computed(() =>
      importResult.value && !isCleanImport(importResult.value) ? importResult.value : null)
    watch(importResult, (result) => {
      if (!isCleanImport(result)) return
      const messages = importSummaryMessages(result)
      const format = line => formatImportSummaryLine(line, t, formatLocalizedNumber)
      toast.add({
        severity: 'success',
        summary: format(messages.title),
        detail: messages.lines.map(format).join(' ') || undefined,
        life: 6000
      })
      dismissImportResult()
    })

    // Watch for import completion to update stats
    watch(hasActiveImportTask, async (isActive, wasActive) => {
      if (!isActive && wasActive) {
        const token = await getAuthToken()
        if (token) {
          fetchUserStats(token)
        }
      }
    })

    // Theme options for SelectButton (computed so a live language switch re-labels them)
    const themeOptions = computed(() => [
      { label: t('app.userMenu.theme.light'), value: 'light', icon: 'pi pi-sun' },
      { label: t('app.userMenu.theme.auto'), value: 'auto', icon: 'pi pi-desktop' },
      { label: t('app.userMenu.theme.dark'), value: 'dark', icon: 'pi pi-moon' }
    ])

    // Computed property for the selected theme option
    const themeOption = computed({
      get: () => themePreference.value,
      set: (value) => {
        if (value !== null && value !== themePreference.value) {
          setTheme(value)
        }
      }
    })

    // Handler for theme change
    const onThemeChange = () => {
      // The change is already handled by the computed property setter
    }

    // Language switching now lives in ProfileSettingsModal (it owns the live
    // apply + PUT). App.vue only syncs backendUser.language when the modal
    // reports a successful change, so other reads stay consistent.
    const onLanguageChangedFromProfile = (value) => {
      if (backendUser.value) {
        backendUser.value = { ...backendUser.value, language: value }
      }
    }

    // Check if current route requires authentication
    const requiresAuth = computed(() => {
      return route.meta.requiresAuth !== false
    })

    // Track current route path
    const currentRoute = computed(() => route.path)

    // Function to toggle sidebar
    const toggleSidebar = () => {
      window.dispatchEvent(new CustomEvent('toggle-sidebar'))
    }

    const WHATS_NEW_ENTRIES = [
      { date: '2026-06-02', title: 'AI Article Summaries', description: 'Pro: every article you save gets an automatic 3–4 bullet summary, shown in the reader and the bookmark menu', icon: 'pi pi-sparkles', docsLink: 'https://linkflare.io/docs/article-summaries/' },
      { date: '2026-05-08', title: 'Highlights', description: 'Mark up any page with the extension — six colors, notes, and a searchable Highlights page', icon: 'pi pi-pencil', docsLink: 'https://linkflare.io/docs/highlights/' },
      { date: '2026-02-27', title: 'Bulk Import (4 sources)', description: 'Import from Pocket, Instapaper, Goodreads & YouTube', icon: 'pi pi-upload' },
      { date: '2026-01-30', title: 'Dashboard & Stats', description: 'Beautiful charts and insights about your library', icon: 'pi pi-chart-pie' },
      { date: '2026-01-15', title: 'Onboarding Flow', description: 'Guided first-run experience for new users', icon: 'pi pi-star' },
      { date: '2026-01-01', title: 'Keyboard Shortcuts', description: 'Full keyboard shortcuts — press ? to see all', icon: 'pi pi-question-circle' },
    ]

    const showWhatsNew = ref(false)
    const whatsNewEntries = WHATS_NEW_ENTRIES

    const hasUnseenWhatsNew = computed(() => {
      try {
        const lastSeen = localStorage.getItem('linkflare_last_seen_changelog')
        if (!lastSeen) return true
        const latestEntry = WHATS_NEW_ENTRIES[0]
        return latestEntry ? new Date(latestEntry.date) > new Date(lastSeen) : false
      } catch {
        return false
      }
    })

    const toggleWhatsNew = () => {
      showWhatsNew.value = !showWhatsNew.value
      if (showWhatsNew.value) {
        showUserMenu.value = false
        try {
          localStorage.setItem('linkflare_last_seen_changelog', new Date().toISOString())
        } catch { }
      }
    }

    const showUserMenu = ref(false)
    const showInvitationsPanel = ref(false)
    const showSubscriptionModal = ref(false)
    const showImportModal = ref(false)
    const showChromeImportModal = ref(false)
    const showUrlListImportModal = ref(false)
    const showPocketImportModal = ref(false)
    const showInstapaperImportModal = ref(false)
    const showGoodreadsImportModal = ref(false)
    const showYoutubeImportModal = ref(false)
    const showKarakeepImportModal = ref(false)
    const showLinkwardenImportModal = ref(false)
    const showExportModal = ref(false)
    const showProfileModal = ref(false)
    const showProtectionPasswordModal = ref(false)
    const showOnboarding = ref(false)
    const importMenuRef = ref(null)
    const newPasswordInputRef = ref(null)
    const protectionPasswordForm = ref({
      password: '',
      confirmPassword: ''
    })
    const protectionPasswordError = ref('')
    const savingProtectionPassword = ref(false)
    const privateModeEnabled = ref(true)

    const mountOnFirstOpen = (source) => {
      const mounted = ref(source.value)
      watch(source, (isOpen) => {
        if (isOpen) {
          mounted.value = true
        }
      })
      return mounted
    }

    const mountOnAnyOpen = (sources) => {
      const mounted = ref(sources.some((source) => source.value))
      watch(sources, (values) => {
        if (values.some(Boolean)) {
          mounted.value = true
        }
      })
      return mounted
    }

    const showSubscriptionModalMounted = mountOnFirstOpen(showSubscriptionModal)
    const showImportModalMounted = mountOnFirstOpen(showImportModal)
    const showChromeImportModalMounted = mountOnFirstOpen(showChromeImportModal)
    const showUrlListImportModalMounted = mountOnFirstOpen(showUrlListImportModal)
    const showImportSourceModalMounted = mountOnAnyOpen([
      showPocketImportModal,
      showInstapaperImportModal,
      showGoodreadsImportModal,
      showYoutubeImportModal,
      showKarakeepImportModal,
      showLinkwardenImportModal
    ])
    const showExportModalMounted = mountOnFirstOpen(showExportModal)
    const showProfileModalMounted = mountOnFirstOpen(showProfileModal)
    const showOnboardingMounted = mountOnFirstOpen(showOnboarding)
    const showShortcutHelpMounted = mountOnFirstOpen(showShortcutHelp)

    // Private mode options for SelectButton (computed for live language switch)
    const privateModeOptions = computed(() => [
      { label: t('app.userMenu.privateMode.all'), value: false, icon: 'pi pi-eye' },
      { label: t('app.userMenu.privateMode.private'), value: true, icon: 'pi pi-lock' }
    ])

    // Computed property for the selected private mode option
    const privateModeOption = computed({
      get: () => privateModeEnabled.value,
      set: (value) => {
        if (value !== privateModeEnabled.value) {
          privateModeEnabled.value = value
          togglePrivateMode()
        }
      }
    })

    // Handler for private mode change
    const onPrivateModeChange = () => {
      // The change is already handled by the computed property setter
    }

    // Every import dialog in the app lives here, so this is the one place that
    // knows which source id opens which modal. Reached from three directions:
    // the avatar menu, the tour's import step, and the empty-state button in
    // Bookmarks.vue (via the OPEN_IMPORT_MODAL_EVENT bridge).
    // Keyed by the same ids as IMPORT_SOURCES so adding a source is one edit in
    // each file rather than an if/else chain that silently no-ops when someone
    // adds the menu entry and forgets the dialog.
    const importModalByType = {
      'url-list': showUrlListImportModal,
      'raindrop': showImportModal,
      'chrome': showChromeImportModal,
      'pocket': showPocketImportModal,
      'instapaper': showInstapaperImportModal,
      'goodreads': showGoodreadsImportModal,
      'youtube': showYoutubeImportModal,
      'karakeep': showKarakeepImportModal,
      'linkwarden': showLinkwardenImportModal,
    }
    const openImportModal = (type) => {
      const modal = importModalByType[type]
      if (modal) modal.value = true
    }

    // Menu model comes from the shared composable so the avatar menu and the
    // empty-state menu can never drift to different source lists.
    const importMenuItems = useImportMenu((id) => {
      openImportModal(id)
      showUserMenu.value = false
    })
    const isLoading = ref(true)

    // Load private mode state from localStorage on mount
    // Default to private (true) if no preference has been saved yet (e.g. new device)
    const loadPrivateModeState = () => {
      try {
        const savedState = localStorage.getItem('linkflare_private_mode_enabled')
        if (savedState !== null) {
          privateModeEnabled.value = savedState === 'true'
        } else {
          privateModeEnabled.value = true
          localStorage.setItem('linkflare_private_mode_enabled', 'true')
        }
      } catch (e) {
        console.warn('Failed to load private mode state from localStorage', e)
        privateModeEnabled.value = true
      }
    }

    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value
      showInvitationsPanel.value = false
    }

    const handleAcceptInvitation = async (invite) => {
      try {
        const token = await getAuthToken()
        if (!token) {
          toast.add({ severity: 'error', summary: t('common.toasts.errorTitle'), detail: t('app.toasts.authRefresh'), life: 5000 })
          return
        }
        await ApiService.acceptInvitation(invite.token, token)
        toast.add({ severity: 'success', summary: t('app.toasts.invitationAcceptedTitle'), detail: t('app.toasts.invitationAcceptedDetail', { name: invite.collection_name }), life: 3000 })
        showInvitationsPanel.value = false
        loadPendingInvitations(token)
        // Notify Bookmarks.vue to reload collections so the new shared collection appears
        window.dispatchEvent(new CustomEvent('invitation-accepted'))
      } catch (e) {
        const msg = e?.message || ''
        const detail = msg.includes('email does not match') ? t('app.toasts.invitationEmailMismatch') : t('app.toasts.invitationAcceptFailed')
        toast.add({ severity: 'error', summary: t('common.toasts.errorTitle'), detail, life: 5000 })
      }
    }

    const handleDeclineInvitation = async (invite) => {
      try {
        const token = await getAuthToken()
        if (!token) {
          toast.add({ severity: 'error', summary: t('common.toasts.errorTitle'), detail: t('app.toasts.authRefresh'), life: 5000 })
          return
        }
        await ApiService.declineInvitation(invite.token, token)
        toast.add({ severity: 'success', summary: t('app.toasts.invitationDeclinedTitle'), life: 2000 })
        loadPendingInvitations(token)
      } catch (e) {
        toast.add({ severity: 'error', summary: t('common.toasts.errorTitle'), detail: t('app.toasts.invitationDeclineFailed'), life: 3000 })
      }
    }

    const handleInvitationAccepted = async () => {
      const token = await getAuthToken()
      if (token) loadPendingInvitations(token)
    }

    const handleSignOut = async () => {
      try {
        const success = await authSignOut()
        if (success) {
          showUserMenu.value = false
          clearApiKey()
          clearSentryUser()
          clearBadge()
          router.push('/sign-in')
        }
      } catch (error) {
        console.error('Error signing out:', error)
      }
    }

    const toggleImportMenu = (event) => {
      if (importMenuRef.value) {
        importMenuRef.value.toggle(event)
      }
    }

    const openExportModal = () => {
      showExportModal.value = true
      showUserMenu.value = false
    }

    const openSubscriptionModal = () => {
      showSubscriptionModal.value = true
      showUserMenu.value = false
    }

    const openProfileModal = () => {
      showProfileModal.value = true
      showUserMenu.value = false
    }

    const onUsernameChanged = (newUsername) => {
      if (backendUser.value) {
        backendUser.value = { ...backendUser.value, username: newUsername }
      }
    }

    const onSubscriptionUpdated = (updatedUser) => {
      if (updatedUser) {
        const merged = { ...(backendUser.value || {}), ...updatedUser }
        // The backend OMITS these keys when null (User.asJson), so a spread can never clear
        // them — a fresh user payload is authoritative for subscription state. Without this,
        // a promo user who buys via StoreKit keeps stale promo UI until a full reload.
        if (!('cancels_at' in updatedUser)) delete merged.cancels_at
        if (!('promo_expires_at' in updatedUser)) delete merged.promo_expires_at
        backendUser.value = merged
      }
    }

    const copyApiKey = async () => {
      const key = backendUser.value?.api_key || localStorage.getItem('linkflare_api_key')
      showUserMenu.value = false
      if (!key) {
        toast.add({ severity: 'error', summary: t('app.toasts.apiKeyNotFoundTitle'), detail: t('app.toasts.apiKeyNotFoundDetail'), life: 3000 })
        return
      }
      try {
        await navigator.clipboard.writeText(key)
        toast.add({ severity: 'success', summary: t('app.toasts.apiKeyCopiedTitle'), detail: t('app.toasts.apiKeyCopiedDetail'), life: 3000 })
      } catch {
        toast.add({ severity: 'error', summary: t('app.toasts.copyFailedTitle'), detail: t('app.toasts.copyFailedDetail'), life: 3000 })
      }
    }

    const openProtectionPasswordModal = () => {
      protectionPasswordForm.value = {
        password: '',
        confirmPassword: ''
      }
      protectionPasswordError.value = ''
      showProtectionPasswordModal.value = true
      showUserMenu.value = false
    }

    const onShowProtectionPasswordModal = () => {
      nextTick(() => {
        setTimeout(() => {
          if (newPasswordInputRef.value) {
            const inputElement = newPasswordInputRef.value.$el || newPasswordInputRef.value
            const actualInput = inputElement.querySelector('input') || inputElement
            if (actualInput && typeof actualInput.focus === 'function') {
              actualInput.focus()
            }
          }
        }, 200)
      })
    }

    const saveProtectionPassword = async () => {
      if (!protectionPasswordForm.value.password || !protectionPasswordForm.value.confirmPassword) {
        return
      }

      if (protectionPasswordForm.value.password !== protectionPasswordForm.value.confirmPassword) {
        protectionPasswordError.value = t('app.protectionPassword.mismatch')
        return
      }

      if (protectionPasswordForm.value.password.length < 4) {
        protectionPasswordError.value = t('app.protectionPassword.tooShort')
        return
      }

      try {
        savingProtectionPassword.value = true
        protectionPasswordError.value = ''

        const token = await getAuthToken()
        if (!token) return

        // Set protection password using the correct API endpoint
        await ApiService.setPrivateModePassword(protectionPasswordForm.value.password, token)

        toast.add({
          severity: 'success',
          summary: t('common.toasts.successTitle'),
          detail: t('app.protectionPassword.savedDetail'),
          life: 3000
        })

        cancelProtectionPassword()
      } catch (err) {
        console.error('Error saving protection password:', err)
        protectionPasswordError.value = t('app.protectionPassword.saveFailed')
      } finally {
        savingProtectionPassword.value = false
      }
    }

    const cancelProtectionPassword = () => {
      showProtectionPasswordModal.value = false
      protectionPasswordForm.value = {
        password: '',
        confirmPassword: ''
      }
      protectionPasswordError.value = ''
    }

    const togglePrivateMode = () => {
      try {
        // Save state to localStorage
        localStorage.setItem('linkflare_private_mode_enabled', privateModeEnabled.value.toString())

        // Emit event to notify other components (like Bookmarks view)
        window.dispatchEvent(new CustomEvent('private-mode-toggled', {
          detail: { enabled: privateModeEnabled.value }
        }))

        toast.add({
          severity: privateModeEnabled.value ? 'info' : 'success',
          summary: privateModeEnabled.value ? t('app.privateModeToasts.hiddenTitle') : t('app.privateModeToasts.shownTitle'),
          detail: privateModeEnabled.value
            ? t('app.privateModeToasts.hiddenDetail')
            : t('app.privateModeToasts.shownDetail'),
          life: 4000
        })
      } catch (e) {
        console.warn('Failed to save private mode state', e)
      }
    }

    const handleImportCompleted = (result) => {
      // Check if this is an async task
      if (result.task_id) {
        // Start tracking the import task
        startImportTask({
          task_id: result.task_id,
          urls_found: result.urls_found
        })
      }

      // Async imports finish later (useImportTask polls); this marks the moment
      // the user handed us a file, which is the step that actually drops off.
      trackEvent('import_started', result.task_id ? { mode: 'async' } : { mode: 'sync' })

      // Emit a global event to notify other components about the import
      // We'll use a simple approach: emit an event on the window object
      window.dispatchEvent(new CustomEvent('bookmarks-imported', {
        detail: { result }
      }))
    }

    // Close user menu / whats-new panel when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-section')) {
        showUserMenu.value = false
      }
      if (!event.target.closest('.invitation-bell')) {
        showInvitationsPanel.value = false
      }
      if (!event.target.closest('.whats-new-section')) {
        showWhatsNew.value = false
      }
    }

    // Navigation handler for keyboard shortcut "g b", "g f", etc.
    const handleNavigate = (event) => {
      const path = event.detail
      if (path && typeof path === 'string') {
        router.push(path)
      }
    }

    // Reminders → toast. Named (not anonymous) so it can be removed on unmount.
    const handleReminder = (e) => {
      const data = e.detail || {}
      toast.add({
        severity: 'info',
        summary: data.title || t('app.toasts.reminderFallbackTitle'),
        detail: data.body,
        life: 10000
      })
    }

    // A lazily-mounted modal whose chunk cannot be fetched renders nothing, so
    // without this the user just sees a button that does nothing. Tell them, and
    // give them the one action that actually fixes it.
    const handleChunkLoadFailure = () => {
      toast.add({
        severity: 'error',
        summary: t('app.chunkLoadFailed.title'),
        detail: t('app.chunkLoadFailed.detail'),
        life: 12000
      })
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      initializeTheme()
      loadPrivateModeState()
      window.addEventListener('open-subscription-modal', openSubscriptionModal)
      window.addEventListener('navigate', handleNavigate)
      window.addEventListener('invitation-accepted', handleInvitationAccepted)
      window.addEventListener('linkflare-reminder', handleReminder)
      window.addEventListener(OPEN_IMPORT_MODAL_EVENT, handleImportRequest)
      window.addEventListener(CHUNK_LOAD_FAILED_EVENT, handleChunkLoadFailure)

    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('open-subscription-modal', openSubscriptionModal)
      window.removeEventListener('navigate', handleNavigate)
      window.removeEventListener('invitation-accepted', handleInvitationAccepted)
      window.removeEventListener('linkflare-reminder', handleReminder)
      window.removeEventListener(OPEN_IMPORT_MODAL_EVENT, handleImportRequest)
      window.removeEventListener(CHUNK_LOAD_FAILED_EVENT, handleChunkLoadFailure)
    })

    // Choosing an import from the tour ENDS the tour: the modal is dismissed
    // here and never comes back in this session, so completion has to be
    // recorded here too — otherwise the tour resurrects at step 1 on the next
    // load, on top of a library the user just filled. Same writer as the
    // tour's own finish/skip path (utils/onboardingCompletion.js).
    const handleOnboardingImport = (type) => {
      showOnboarding.value = false
      markOnboardingComplete(getAuthToken)
      openImportModal(type)
    }

    // Views outside this shell (the Bookmarks empty state) ask for an import
    // dialog through a window event, mirroring `open-subscription-modal`.
    const handleImportRequest = (event) => {
      openImportModal(event?.detail?.type)
    }

    // Watch for authentication state changes
    let userLoadedOnce = false

    watch([isLoaded, isSignedIn], async ([loaded, signedIn]) => {
      if (loaded) {
        isLoading.value = false

        if (!signedIn && router.currentRoute.value.meta.requiresAuth) {
          router.push('/sign-in')
        } else if (signedIn && (router.currentRoute.value.path === '/sign-in' || router.currentRoute.value.path === '/sign-up')) {
          // Only redirect from the exact sign-in/sign-up pages, not from SSO callback sub-paths
          // Restore the last visited route if available (so the user lands where they left off).
          // A cold launch at '/' restores in the router's '/' redirect instead (lastRoute.js).
          router.push(getSavedLastRoute(router) || '/bookmarks')
        }

        // After first time we detect signed in, load user from backend to obtain api_key
        if (signedIn && !userLoadedOnce) {
          let loadedSuccessfully = false
          try {
            const token = await getAuthToken()
            if (token) {
              // Fetch user stats (bookmark and feed counts)
              fetchUserStats(token)
              // Load pending collaboration invitations
              loadPendingInvitations(token)
              // Start polling for collection activity notifications
              startActivityPolling()

              const resp = await ApiService.getUser(token)
              if (resp && resp.user) {
                backendUser.value = resp.user
                setSentryUser({ id: resp.user.id, email: resp.user.email, username: resp.user.username })
                if (resp.user.api_key) {
                  try {
                    localStorage.setItem('linkflare_api_key', resp.user.api_key)
                  } catch (e) {
                    console.warn('Failed to store api key in localStorage', e)
                  }
                  // Also set a domain-wide cookie so browser extensions can authenticate
                  // via credentials:'include' without relying on chrome.storage
                  try {
                    const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1'
                    const cookieDomain = isLocalhost ? '' : '; domain=.linkflare.io'
                    const secure = isLocalhost ? '' : '; Secure'
                    document.cookie = `linkflare_api_key=${resp.user.api_key}${cookieDomain}; path=/; max-age=31536000; SameSite=None${secure}`
                  } catch (e) {
                    console.warn('Failed to set api key cookie', e)
                  }
                }

                // Language sync (i18n): the server value wins once set (cross-
                // device sync). NULL + a /sign-up capture marker → write the
                // RESOLVED locale once (not the raw capture — protects an
                // existing-EN user merely passing through /sign-up with a German
                // browser). NULL without marker (pre-feature users) → no-op, so
                // existing accounts never flip language unexpectedly.
                const serverLanguage = resp.user.language || null
                if (serverLanguage) {
                  clearSignupLanguage() // server already knows; the marker is spent
                  if (serverLanguage !== currentLocale.value) {
                    await setAppLocale(serverLanguage)
                  }
                } else if (peekSignupLanguage()) {
                  // NULL + signup marker → persist the RESOLVED locale once. Keep
                  // the marker until the PUT succeeds so a failed write retries on
                  // the next load instead of silently leaving the server at NULL.
                  ApiService.updateLanguage(currentLocale.value, token).then(() => {
                    clearSignupLanguage()
                    if (backendUser.value) {
                      backendUser.value = { ...backendUser.value, language: currentLocale.value }
                    }
                  }).catch(() => { /* keep marker; retry on next load */ })
                }
              }
              loadedSuccessfully = true

              // Show onboarding for first-time users (DB-backed, works across devices)
              // Also check localStorage as fallback — if the user already skipped/completed
              // but the API call failed (e.g. missing auth token), don't re-show the tour
              // `onboarding_completed` alone is NOT a signup signal: it is also
              // false for every long-standing account that skipped or abandoned
              // the tour, so on the first load after a deploy all of them would
              // emit a signup — a spike indistinguishable from real acquisition
              // and permanent, because trackOnce writes localStorage. Clerk's
              // createdAt is the only real account-age signal we have client
              // side; require both, and treat "unknown age" as "not new".
              // Ad/tool attribution is not attached here — it is already
              // recorded server-side on users.signup_referrer.
              const accountCreatedAt = user.value?.createdAt ? new Date(user.value.createdAt).getTime() : NaN
              const isNewAccount = Number.isFinite(accountCreatedAt)
                && Date.now() - accountCreatedAt < SIGNUP_MAX_ACCOUNT_AGE_MS
              if (isNewAccount && resp.user && !resp.user.onboarding_completed) {
                trackOnce('signup', 'signup')
              }

              const forceOnboarding = new URLSearchParams(window.location.search).has('onboarding')
              if (forceOnboarding || (resp.user && !resp.user.onboarding_completed)) {
                if (isOnboardingCompletedLocally()) {
                  // localStorage says done but DB disagrees — sync to backend
                  const token = await getAuthToken()
                  ApiService.markOnboardingCompleted(token).catch(() => { })
                } else {
                  showOnboarding.value = true
                }
                if (forceOnboarding) {
                  showOnboarding.value = true
                }
              }

              // ?import=<source> deep link, sent by the marketing site's free
              // bookmarks tool so its "Import into Linkflare" button lands on
              // the importer instead of an empty library. Captured and stripped
              // from the URL at boot (main.js), so this consume is the only
              // thing between one arrival and one opened dialog.
              // Resolved AFTER the onboarding decision above, so we know whether
              // the tour is about to cover the screen: if it is, go through the
              // tour's own import path, which dismisses it and records
              // completion together (see handleOnboardingImport — a dialog
              // opened behind the 8-step overlay would be unreachable, and a
              // tour dismissed without recording resurrects at step 1).
              const importIntent = consumeImportIntent()
              if (importIntent) {
                if (showOnboarding.value) {
                  handleOnboardingImport(importIntent)
                } else {
                  openImportModal(importIntent)
                }
              }
            }
          } catch (e) {
            console.error('Failed to load user from backend', e)

            // Use the global error handler for 401 errors
            const handled = await handleApiError(e, 'user load')
            if (handled) {
              userLoadedOnce = false
              return
            }
          } finally {
            if (loadedSuccessfully) {
              userLoadedOnce = true
            }
          }
        } else if (!signedIn) {
          // ensure api key cleared if user signs out elsewhere
          clearApiKey()
          // every sign-out lands here (menu, other tab, 401, account deletion),
          // so the next account on this browser never sees this one's import
          forgetImport()
          userLoadedOnce = false
        }
      }
    }, { immediate: true })

    return {
      runUndo,
      isLoading,
      isSignedIn,
      requiresAuth,
      user,
      userDisplayName,
      userEmail,
      isPro,
      isAdmin,
      cancelsAt,
      promoExpiresAt,
      showUserMenu,
      showInvitationsPanel,
      pendingInvitations,
      activityNotifications,
      bellBadgeCount,
      toggleNotificationsPanel,
      navigateToActivityLink,
      dismissActivity,
      dismissAllActivity,
      formatRelativeTime,
      handleAcceptInvitation,
      handleDeclineInvitation,
      showSubscriptionModal,
      showSubscriptionModalMounted,
      showProfileModal,
      showProfileModalMounted,
      openProfileModal,
      onUsernameChanged,
      onLanguageChangedFromProfile,
      backendUser,
      showImportModal,
      showImportModalMounted,
      showChromeImportModal,
      showChromeImportModalMounted,
      showUrlListImportModal,
      showUrlListImportModalMounted,
      showPocketImportModal,
      showInstapaperImportModal,
      showGoodreadsImportModal,
      showYoutubeImportModal,
      showKarakeepImportModal,
      showLinkwardenImportModal,
      showImportSourceModalMounted,
      showExportModal,
      showExportModalMounted,
      showProtectionPasswordModal,
      importMenuItems,
      importMenuRef,
      newPasswordInputRef,
      protectionPasswordForm,
      protectionPasswordError,
      savingProtectionPassword,
      privateModeEnabled,
      currentRoute,
      // Import task tracking
      currentImportTask,
      hasActiveImportTask,
      importProgressPercent,
      urlsImported,
      estimatedTimeRemaining,
      cancelImportTask,
      importSummaryToShow,
      dismissImportResult,
      // Methods
      toggleUserMenu,
      handleSignOut,
      toggleImportMenu,
      openExportModal,
      openSubscriptionModal,
      onSubscriptionUpdated,
      copyApiKey,
      openProtectionPasswordModal,
      onShowProtectionPasswordModal,
      saveProtectionPassword,
      cancelProtectionPassword,
      togglePrivateMode,
      handleImportCompleted,
      toggleSidebar,
      isDarkMode,
      toggleTheme,
      themeOptions,
      themeOption,
      onThemeChange,
      privateModeOptions,
      privateModeOption,
      onPrivateModeChange,
      // Keyboard shortcuts
      showShortcutHelp,
      showShortcutHelpMounted,
      shortcutMap,
      openKeyboardShortcuts,
      openChangelog,
      navigateToStats,
      navigateToTags,
      navigateToRules,
      navigateToNotifications,
      // Onboarding
      showOnboarding,
      showOnboardingMounted,
      handleOnboardingImport,
      // What's New
      showWhatsNew,
      hasUnseenWhatsNew,
      toggleWhatsNew,
      whatsNewEntries,
    }
  }
}
</script>

<style scoped>
.layout-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  width: 100%;
}

.app-header {
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 2px 4px var(--shadow-light);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: background-color 0.3s ease, border-color 0.3s ease;
  width: 100%;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  max-width: 1600px;
  margin: 0 auto;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.logo h1 {
  color: var(--accent-color);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.header-logo {
  height: 40px;
  display: block;
}

.header-logo-icon {
  display: none;
}

.sidebar-toggle-btn-mobile {
  display: none;
  color: var(--text-secondary) !important;
  transition: all 0.3s ease !important;
}

.sidebar-toggle-btn-mobile:hover {
  color: var(--accent-color) !important;
  background-color: var(--bg-primary) !important;
}

.main-nav {
  display: flex;
  gap: 1rem;
  flex-shrink: 0;
  min-width: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: var(--text-secondary);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.nav-link:hover {
  color: var(--accent-color);
  background-color: var(--bg-primary);
}

.nav-link.active {
  color: var(--accent-color);
  background-color: var(--accent-light);
}

.nav-link .nav-icon {
  width: 1em;
  height: 1em;
}

.nav-pro-badge {
  font-size: 0.55rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
  letter-spacing: 0.03em;
  line-height: 1;
  flex-shrink: 0;
}

.search-section {
  flex: 1 1 12rem;
  max-width: 400px;
  min-width: 0;
  margin: 0 1rem;
}

@media (max-width: 1450px) {
  .header-content {
    padding: 0.75rem 1rem;
    gap: 0.75rem;
  }

  .main-nav {
    gap: 0.5rem;
  }

  .nav-link {
    padding: 0.5rem 0.75rem;
  }

  .search-section {
    max-width: 280px;
    margin: 0 0.25rem;
  }
}

@media (max-width: 1200px) {
  .header-content {
    padding: 0.75rem 1rem;
    gap: 0.75rem;
  }

  .search-section {
    margin: 0 0.5rem;
  }
}

@media (max-width: 1100px) {
  .header-logo-text {
    display: none;
  }

  .header-logo-icon {
    display: block;
    height: 32px;
  }
}


@media (max-width: 768px) {
  .nav-label {
    display: none;
  }

  .nav-pro-badge {
    display: none;
  }

  .nav-link {
    padding: 0.5rem;
  }
}

.app-main {
  flex: 1;
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.loading-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
}

.loading-spinner {
  color: var(--accent-color);
}

.user-section {
  position: relative;
  flex-shrink: 0;
}

.whats-new-section {
  position: relative;
  flex-shrink: 0;
}

.whats-new-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 1.1rem;
  transition: all 0.2s ease;
}

.whats-new-btn:hover {
  color: var(--accent-color);
  background: var(--bg-primary);
}

.whats-new-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
  border: 2px solid var(--bg-secondary);
}

.whats-new-panel {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  box-shadow: 0 8px 24px var(--shadow-light), 0 2px 8px var(--shadow-medium);
  min-width: 280px;
  max-width: 320px;
  z-index: 9999;
  margin-top: 0.5rem;
  padding: 0.75rem 0;
  overflow: hidden;
}

.whats-new-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem 0.5rem;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 0.25rem;
}

.whats-new-panel-header strong {
  color: var(--text-primary);
  font-size: 0.875rem;
}

.whats-new-see-all {
  font-size: 0.75rem;
  color: var(--accent-color);
  text-decoration: none;
}

.whats-new-see-all:hover {
  text-decoration: underline;
}

.whats-new-entry {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.6rem 1rem;
  transition: background 0.15s ease;
}

.whats-new-entry:hover {
  background: var(--bg-primary);
}

.whats-new-entry-icon {
  font-size: 0.9rem;
  color: var(--accent-color);
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.whats-new-entry-body {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.whats-new-entry-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
}

.whats-new-entry-desc {
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.4;
}

.user-button {
  color: var(--text-secondary) !important;
  transition: all 0.3s ease !important;
}

.user-button:hover {
  color: var(--accent-color) !important;
  background-color: var(--bg-primary) !important;
}

.user-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px var(--shadow-light), 0 2px 4px -1px var(--shadow-medium);
  min-width: 225px;
  max-height: calc(100vh - 60px);
  overflow-y: auto;
  z-index: 9999;
  margin-top: 0.5rem;
  padding: 1rem;
  transition: all 0.3s ease;
}

.user-menu-close-btn {
  display: none;
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  align-items: center;
  justify-content: center;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.user-info strong {
  color: var(--accent-color);
  font-weight: 600;
}

.user-info span {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.username-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.1rem;
}

.username-display {
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-family: monospace;
}

.user-menu hr {
  border: none;
  border-top: 1px solid var(--border-color);
  margin: 0.75rem 0;
}

.theme-toggle-section {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.5rem 0;
}

.theme-toggle {
  width: 100% !important;
}

.theme-toggle :deep(.p-selectbutton) {
  display: flex;
  width: 100%;
}

.theme-toggle :deep(.p-button) {
  flex: 1;
  justify-content: center;
  font-size: 1rem !important;
  padding: 0.5rem 1rem;
}

.theme-toggle :deep(.p-button .pi) {
  font-size: 1rem;
}

.theme-toggle :deep(.p-button:first-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.theme-toggle :deep(.p-button:not(:first-child):not(:last-child)) {
  border-radius: 0;
}

.theme-toggle :deep(.p-button:last-child) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.private-mode-section {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.5rem 0;
}

.private-mode-toggle {
  width: 100% !important;
}

.private-mode-toggle :deep(.p-selectbutton) {
  display: flex;
  width: 100%;
}

.private-mode-toggle :deep(.p-button) {
  flex: 1;
  justify-content: center;
  font-size: 0.875rem !important;
  padding: 0.5rem 1rem;
  gap: 0.5rem;
}

.private-mode-toggle :deep(.p-button .pi) {
  font-size: 1rem;
}

.private-mode-toggle :deep(.p-button .option-label) {
  font-size: 0.875rem;
}

.private-mode-toggle :deep(.p-button:first-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.private-mode-toggle :deep(.p-button:last-child) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.import-button {
  width: 100% !important;
  justify-content: flex-start !important;
  text-align: left !important;
}

.import-button :deep(.p-button-icon) {
  margin-right: 0.5rem;
}

.import-menu-wrapper {
  position: relative;
}

.user-menu-action-btn {
  width: 100% !important;
  justify-content: flex-start !important;
  text-align: left !important;
  color: var(--accent-color) !important;
}

.user-menu-action-btn:hover {
  background-color: var(--accent-light) !important;
}

.user-menu-action-btn :deep(.p-button-icon) {
  color: var(--accent-color) !important;
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .header-content {
    padding: 0.75rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
    row-gap: 0.75rem;
  }

  .sidebar-toggle-btn-mobile {
    display: flex;
    order: 0;
    margin-right: 0;
    padding: 0.5rem !important;
  }

  .logo {
    order: 1;
    margin-right: 0.25rem;
  }

  .header-logo-text {
    display: none;
  }

  .header-logo-icon {
    display: block;
    height: 28px;
  }

  .main-nav {
    order: 2;
    gap: 0.5rem;
    margin-right: auto;
  }

  .search-section {
    order: 5;
    margin: 0;
    max-width: 100%;
    width: 100%;
    flex-basis: 100%;
  }

  .user-section {
    order: 4;
    margin-left: 0;
  }

  .nav-link {
    padding: 0.375rem 0.5rem;
    font-size: 0.9rem;
  }

  .nav-label {
    display: none;
  }

  .user-button {
    padding: 0.5rem !important;
  }

  .app-main {
    padding: 1rem;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .user-menu {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin-top: 0;
    border-radius: 0;
    border: none;
    min-width: unset;
    max-height: 100vh;
    overflow-y: auto;
    padding: 1rem;
    -webkit-overflow-scrolling: touch;
  }

  .user-menu-close-btn {
    display: flex;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 0.5rem 0.5rem;
    gap: 0.25rem;
  }

  .main-nav {
    gap: 0.25rem;
  }

  .nav-link {
    padding: 0.375rem 0.375rem;
  }

  .sidebar-toggle-btn-mobile {
    padding: 0.375rem !important;
  }

  .user-button {
    padding: 0.375rem !important;
  }
}

.protection-password-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modal-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field label {
  font-weight: 500;
  color: var(--text-primary);
}

.password-error {
  color: #e24c4c;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  margin-bottom: 0;
}

.w-full {
  width: 100%;
}

.pro-badge {
  margin-top: 0.5rem;
  background-image: url('/lush-forest.jpeg');
  background-size: cover;
  background-position: center;
  color: white;
  padding: 0.35rem 1rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  width: 100%;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

.pro-badge i {
  font-size: 1.1rem;
  position: relative;
  top: 2px;
}


.limit-reached-btn {
  opacity: 0.6;
  cursor: pointer !important;
}

/* Invitation Bell */
.invitation-bell {
  position: relative;
  display: inline-flex;
}

.invitation-bell-btn {
  color: var(--text-secondary) !important;
  width: 2.25rem !important;
  height: 2.25rem !important;
  border-radius: 50% !important;
}

.invitation-bell-btn:hover {
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.05)) !important;
  color: var(--text-primary) !important;
}

.invitation-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: var(--red-500, #ef4444);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  pointer-events: none;
}

.invitations-panel {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 4px 24px var(--shadow-light);
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  padding: 0.5rem;
}

.invitations-panel h4 {
  color: var(--text-primary);
  padding: 0.25rem 0.5rem 0.5rem;
}

.invitation-card {
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.05));
  margin-bottom: 0.25rem;
}

.invitation-card:last-child {
  margin-bottom: 0;
}

.invitation-card-info {
  margin-bottom: 0.5rem;
}

.invitation-card-info strong {
  font-size: 0.875rem;
  display: block;
  margin-bottom: 0.15rem;
  color: var(--text-primary);
}

.invitation-card-detail {
  color: var(--text-secondary);
  font-size: 0.75rem;
}

.invitation-card-actions {
  display: flex;
  gap: 0.25rem;
}

.invitation-card-actions .p-button {
  font-size: 0.8rem !important;
  padding: 0.25rem 0.5rem !important;
}

/* Activity section in bell panel */
.activity-section {
  margin-bottom: 0.25rem;
}

.activity-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 0.25rem;
}

.activity-section-header h4 {
  margin: 0;
}

.mark-all-seen-btn {
  background: none;
  border: none;
  font-size: 0.7rem;
  color: var(--primary-color, #3b82f6);
  cursor: pointer;
  padding: 0.1rem 0.25rem;
  white-space: nowrap;
}

.mark-all-seen-btn:hover {
  text-decoration: underline;
}

.activity-section h4,
.invitations-section h4 {
  color: var(--text-primary);
  padding: 0.25rem 0.5rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
}

.invitations-section {
  border-top: 1px solid var(--border-color);
  padding-top: 0.25rem;
}

.activity-card {
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.05));
  margin-bottom: 0.25rem;
  cursor: pointer;
  transition: background 0.15s;
}

.activity-card:hover {
  background: var(--bg-hover, rgba(0, 0, 0, 0.08));
}

.activity-card-body {
  flex: 1;
  min-width: 0;
}

.activity-card-message {
  font-size: 0.8rem;
  color: var(--text-primary);
  line-height: 1.35;
}

.activity-card-time {
  font-size: 0.7rem;
  color: var(--text-secondary);
  margin-top: 0.2rem;
}

.activity-dismiss-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  font-size: 1rem;
  line-height: 1;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0 0.1rem;
  opacity: 0.5;
  transition: opacity 0.15s;
  align-self: flex-start;
  margin-top: -0.05rem;
}

.activity-dismiss-btn:hover {
  opacity: 1;
}

.view-all-link {
  display: block;
  width: 100%;
  text-align: center;
  font-size: 0.75rem;
  color: var(--primary-color, #3b82f6);
  background: none;
  border: none;
  padding: 0.4rem 0;
  cursor: pointer;
}

.view-all-link:hover {
  text-decoration: underline;
}

.empty-panel {
  padding: 1rem;
  text-align: center;
}

.empty-panel p {
  color: var(--text-secondary);
  font-size: 0.8rem;
}
</style>

<style>
/* group="undo" toast is teleported to <body>: non-scoped on purpose.
 *
 * `pointer-events` here is load-bearing, not cosmetic. A PrimeVue toast
 * container is always in the DOM at z-index 99999, even with no messages, and
 * defaults to `pointer-events: auto`. The default top-right toast collapses to
 * height 0 so it never blocks anything — but style.css pins EVERY toast to
 * `top: 50px !important` below 768px, and this one's bottom-center position
 * adds its own inline `bottom`, so the container got both offsets and stretched
 * to fill the viewport (measured 338x742 on a 375x812 screen). That invisible
 * box swallowed every tap on the page: view settings closed on any press and
 * card long-press never fired. So: the container is transparent to input, the
 * message re-enables it (the Undo button must stay clickable), and below 768px
 * the bottom offset is dropped so the container collapses like the default one
 * and the toast lands top-centre with every other toast on mobile.
 */
.p-toast.undo-toast {
  pointer-events: none;
}

.p-toast.undo-toast .p-toast-message {
  border-radius: 12px;
  pointer-events: auto;
}

@media screen and (max-width: 768px) {
  /* !important is required: PrimeVue writes `bottom` as an inline style. */
  .p-toast.undo-toast {
    bottom: auto !important;
  }
}

.undo-toast-body {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.5rem 0.6rem 0.9rem;
  min-width: 260px;
}

.undo-toast-icon {
  color: var(--green-500, #22c55e);
}

.undo-toast-text {
  flex: 1;
  min-width: 0;
  font-size: 0.9rem;
}

.undo-toast-button {
  font-weight: 600;
}
</style>
